import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Package,
  Wrench,
  FolderTree,
  MapPin,
  Truck,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Activity,
  Clock,
  ArrowLeftRight,
  Users,
  ShoppingCart,
  RefreshCw,
  Zap,
  Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertContext';
import { useSettings } from '../context/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const { isAdmin, canViewPrices } = useAuth();
  const { pendingAlerts } = useAlerts();
  const { formatCurrency } = useSettings();
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, realtimeRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/realtime`)
      ]);
      setStats(statsRes.data);
      setRealtime(realtimeRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
    toast.success('Dashboard refreshed');
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header with Real-time indicator */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Real-time inventory overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm text-xs">
            <Activity className="w-3 h-3 mr-1 text-green-500 animate-pulse" />
            Live
          </Badge>
          {lastUpdate && (
            <span className="text-xs text-slate-400">
              Updated {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={handleRefresh} className="rounded-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Today's Activity - Real-time Section */}
      {realtime && (
        <Card className="border-slate-200 shadow-sm rounded-sm bg-gradient-to-r from-slate-50 to-white" data-testid="today-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Today's Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                <p className="text-xs text-slate-500 uppercase">Issuances</p>
                <p className="text-2xl font-black text-slate-900">{realtime.today?.issuances || 0}</p>
              </div>
              <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                <p className="text-xs text-slate-500 uppercase">Transfers</p>
                <p className="text-2xl font-black text-slate-900">{realtime.today?.transfers || 0}</p>
              </div>
              <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                <p className="text-xs text-slate-500 uppercase">Parts Used</p>
                <p className="text-2xl font-black text-slate-900">{realtime.today?.parts_used || 0}</p>
              </div>
              {canViewPrices() && realtime.today?.revenue !== undefined && (
                <>
                  <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                    <p className="text-xs text-slate-500 uppercase">Revenue</p>
                    <p className="text-2xl font-black text-emerald-600">{formatCurrency(realtime.today?.revenue || 0)}</p>
                  </div>
                  <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                    <p className="text-xs text-slate-500 uppercase">Cost</p>
                    <p className="text-2xl font-black text-red-600">{formatCurrency(realtime.today?.cost || 0)}</p>
                  </div>
                  <div className="bg-white p-3 rounded-sm border border-slate-200 shadow-sm">
                    <p className="text-xs text-slate-500 uppercase">Profit</p>
                    <p className={`text-2xl font-black ${(realtime.today?.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(realtime.today?.profit || 0)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts Section */}
      {realtime && (realtime.alerts?.low_stock_count > 0 || realtime.alerts?.pending_pos > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {realtime.alerts?.low_stock_count > 0 && (
            <Card className="border-red-200 bg-red-50 shadow-sm rounded-sm" data-testid="low-stock-alert">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-bold text-red-800">{realtime.alerts.low_stock_count} Low Stock Items</p>
                      <p className="text-xs text-red-600">
                        {realtime.alerts.low_stock_items?.map(i => i.reference).slice(0, 3).join(', ')}
                        {realtime.alerts.low_stock_items?.length > 3 ? '...' : ''}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => navigate('/parts?low_stock=true')} className="rounded-sm">
                    View All
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {realtime.alerts?.pending_pos > 0 && (
            <Card className="border-amber-200 bg-amber-50 shadow-sm rounded-sm" data-testid="pending-po-alert">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-800">{realtime.alerts.pending_pos} POs Pending Approval</p>
                      <p className="text-xs text-amber-600">Requires manager review</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate('/purchase-orders')} className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-100">
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card 
          className="border-slate-200 shadow-sm rounded-sm card-hover cursor-pointer" 
          data-testid="total-parts-card"
          onClick={() => navigate('/parts')}
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Total Parts</p>
                <p className="text-xl md:text-3xl font-black text-slate-900 mt-1">{stats?.total_parts || 0}</p>
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Package className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 shadow-sm rounded-sm card-hover cursor-pointer" 
          data-testid="total-tools-card"
          onClick={() => navigate('/tools')}
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Total Tools</p>
                <p className="text-xl md:text-3xl font-black text-slate-900 mt-1">{stats?.total_tools || 0}</p>
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Wrench className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 shadow-sm rounded-sm card-hover cursor-pointer" 
          data-testid="locations-card"
          onClick={() => navigate('/locations')}
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Locations</p>
                <p className="text-xl md:text-3xl font-black text-slate-900 mt-1">{stats?.total_locations || 0}</p>
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 shadow-sm rounded-sm card-hover cursor-pointer" 
          data-testid="suppliers-card"
          onClick={() => navigate('/suppliers')}
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Suppliers</p>
                <p className="text-xl md:text-3xl font-black text-slate-900 mt-1">{stats?.total_suppliers || 0}</p>
              </div>
              <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Truck className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Value and Profit Row - Only visible to price viewers */}
      {canViewPrices() && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-slate-200 shadow-sm rounded-sm card-hover" data-testid="stock-value-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Stock Cost</p>
                <p className="text-lg md:text-2xl font-black text-slate-900 truncate">
                  {formatCurrency(stats?.total_stock_value || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm rounded-sm card-hover" data-testid="potential-revenue-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Revenue</p>
                <p className="text-lg md:text-2xl font-black text-blue-600 truncate">
                  {formatCurrency(stats?.total_potential_revenue || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm rounded-sm card-hover" data-testid="profit-margin-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Profit</p>
                <p className="text-lg md:text-2xl font-black text-purple-600">
                  {stats?.overall_profit_margin || 0}%
                </p>
                <p className="text-[10px] md:text-xs text-slate-500 truncate">{formatCurrency(stats?.total_profit || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-sm rounded-sm cursor-pointer transition-all ${
            (stats?.low_stock_count || 0) > 0 ? 'border-red-500 bg-red-50' : 'border-slate-200'
          }`}
          onClick={() => navigate('/parts?low_stock=true')}
          data-testid="low-stock-card"
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
                (stats?.low_stock_count || 0) > 0 ? 'bg-red-100' : 'bg-slate-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 md:w-6 md:h-6 ${
                  (stats?.low_stock_count || 0) > 0 ? 'text-red-600' : 'text-slate-400'
                }`} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Low Stock</p>
                <p className={`text-lg md:text-2xl font-black ${
                  (stats?.low_stock_count || 0) > 0 ? 'text-red-600 pulse-alert' : 'text-slate-900'
                }`}>
                  {stats?.low_stock_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Low Stock Card for Technicians (no financial row) */}
      {!canViewPrices() && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card 
          className={`border-2 shadow-sm rounded-sm cursor-pointer transition-all ${
            (stats?.low_stock_count || 0) > 0 ? 'border-red-500 bg-red-50' : 'border-slate-200'
          }`}
          onClick={() => navigate('/parts?low_stock=true')}
          data-testid="low-stock-card"
        >
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
                (stats?.low_stock_count || 0) > 0 ? 'bg-red-100' : 'bg-slate-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 md:w-6 md:h-6 ${
                  (stats?.low_stock_count || 0) > 0 ? 'text-red-600' : 'text-slate-400'
                }`} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Low Stock</p>
                <p className={`text-lg md:text-2xl font-black ${
                  (stats?.low_stock_count || 0) > 0 ? 'text-red-600 pulse-alert' : 'text-slate-900'
                }`}>
                  {stats?.low_stock_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Recent Activity - Admin Only */}
      {isAdmin() && realtime?.recent_activity && realtime.recent_activity.length > 0 && (
        <Card className="border-slate-200 shadow-sm rounded-sm" data-testid="recent-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {realtime.recent_activity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="rounded-sm text-xs capitalize">
                      {activity.action}
                    </Badge>
                    <span className="text-sm font-medium">{activity.entity_name}</span>
                    <span className="text-xs text-slate-500">({activity.entity_type})</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{activity.user_name}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
